import React from "react";
import styled from "styled-components";
import { ISelectionStats, ScoredSelectionStats, SurveyReportDataSet } from '../../../../../../amorg-common';
import { Card } from "../../../../../../designSystem/Card";
import { SurveyQuestion, Survey } from '../../../../../../amorg-common';
import { RadialPctChart } from "../../../../../../designSystem/graphs/RadialPctChart";
import { H3 } from "../../../../../../designSystem/typography/Heading";
import { Label } from "../../../../../../designSystem/Label";
import { colors } from "../../../../../../designSystem/colors/color";
import { SegmentRatioBar } from "../../../../../../designSystem/graphs/SegmentRatioBar";
import { Em } from "../../../../../../designSystem/typography/Em";
import { QuestionReportDataSet } from '../../../../../../amorg-common/modules/survey/SurveyReportBuilder';
import { txt } from "../../../../../../txt";
import { PageSection } from "../../../../../../designSystem/PageSection";


type ReportSummarySectionProps = {
    survey: Survey,
    data: SurveyReportDataSet,
    excludeQuestionSummary?:boolean,
    expandable?:boolean
}
export function ReportSummarySection(props: ReportSummarySectionProps): JSX.Element 
{
    const allQuestionData = props.data.themeData.flatMap(t => t.questions);

    const answers = allQuestionData.length > 0
        ? allQuestionData[0].stats.totalSelections
        : 0;

    function format(value: number)
    {
        return parseFloat(value.toFixed(1));
    }

    return (
        <div>
            <PageSection
                header={{
                    title: 'Resultat for undersøgelsen',
                    defaultCollapsed: false,
                }}>
                    <div>
                        <H3>
                            {answers === 0 
                                ? <><Em>Ingen</Em> besvarelser</> 
                                : <><Em>{answers}</Em> besvarelser med en gennemsnitlig score på <Em>{props.data.stats.score.agreePlusPct.toFixed(0)}%</Em></>
                            }
                        </H3>
                    </div>
                    <div style={{display:'flex', gap:'20px', alignItems:'center', marginTop:'10px'}}>
                        <RadialPctChart pct={props.data.stats.score.agreePlusPct} />
                        <div style={{flexGrow:1}}>
                            {/* <AnswerRatioVerticalGraph summaryStats={aggregatedSelections} /> */}

                            <SegmentRatioBar legend heightPx={16}
                                data={[
                                    { value: format(props.data.stats.score.summary.score1Count / props.data.stats.totalSelections * 100), color: colors.graphs.survey.score.score1, legendText:txt.SurveyScore_1 },
                                    { value: format(props.data.stats.score.summary.score2Count / props.data.stats.totalSelections * 100), color: colors.graphs.survey.score.score2, legendText:txt.SurveyScore_2 },
                                    { value: format(props.data.stats.score.summary.score3Count / props.data.stats.totalSelections * 100), color: colors.graphs.survey.score.score3, legendText:txt.SurveyScore_3 },
                                    { value: format(props.data.stats.score.summary.score4Count / props.data.stats.totalSelections * 100), color: colors.graphs.survey.score.score4, legendText:txt.SurveyScore_4 },
                                    { value: format(props.data.stats.score.summary.score5Count / props.data.stats.totalSelections * 100), color: colors.graphs.survey.score.score5, legendText:txt.SurveyScore_5 }
                                ]}
                            />

                        </div>
                        {/* <SelectionStatsGraphics stats={aggregatedSelections} /> */}
                    </div>

                    {(props.excludeQuestionSummary===undefined || props.excludeQuestionSummary===false) &&
                        <div style={{marginTop:'10px'}}>
                            <RankedQuestionSummary allQuestions={allQuestionData} />
                        </div>
                    }
                
            </PageSection>
        </div>
    )
}


type RankedQuestionReport = {
    question: SurveyQuestion,
    report: ISelectionStats,
    average: number,
    questionIndex: number
};

type RankedQuestionSummaryProps = { allQuestions: QuestionReportDataSet[] }
function RankedQuestionSummary({ allQuestions }: RankedQuestionSummaryProps): JSX.Element 
{
    const shouldDisplay = allQuestions.length > 5
        && allQuestions[0].stats.totalSelections > 5;

    const rankedQuestions: RankedQuestionReport[] = allQuestions
        .filter(q => q.stats instanceof ScoredSelectionStats)
        .map((v, i) => {
            return {
                question: v.question,
                report: v.stats,
                average: (v.stats as ScoredSelectionStats).score.selectionAverage,
                questionIndex: i
            }
        })
        .sort((a, b) => b.average - a.average);

    return (
        <>
            {shouldDisplay &&
                <div style={{display:'grid', gridTemplateRows:'20px 1fr', gridAutoRows:'1fr', gridTemplateColumns:'1fr 1fr', columnGap:'10px', rowGap:'5px', alignContent:'stretch'}}>
                    <Label>
                        De mest positivt besvarede spørgsmål
                    </Label>
                    <Label>
                        De mindst positivt besvarede spørgsmål
                    </Label>
                    <QuestionContainer question={rankedQuestions[0]} />
                    <QuestionContainer question={rankedQuestions[rankedQuestions.length-1]} />
                    <QuestionContainer question={rankedQuestions[1]} />
                    <QuestionContainer question={rankedQuestions[rankedQuestions.length-2]} />
                    <QuestionContainer question={rankedQuestions[2]} />
                    <QuestionContainer question={rankedQuestions[rankedQuestions.length-3]} />
                </div>
            }
        </>
    );
}

type QuestionContainerProps = {
    question:RankedQuestionReport
}
function QuestionContainer(props:QuestionContainerProps):JSX.Element
{
    return (
        <Card variant="outlined">
            <QuestionHeader>
                #{props.question.questionIndex + 1} med et gennemsnit på <b>({props.question.average.toFixed(1)}/5)</b>
            </QuestionHeader>
            <QuestionText>
                {"\"" + props.question.question.title + "\""}
            </QuestionText>
        </Card>
    );
}

const QuestionHeader = styled.div`
    font-size: 12px;
    margin-bottom: 10px;
`
const QuestionText = styled.div`
    font-size: 12px;
    font-style:italic;
    /* correct way of breaking words and preserving white space */
    overflow-wrap: break-word;
    white-space: pre-wrap;
`